import React from "react";
import {
  FaCheck,
  FaEnvelope,
  FaEye,
  FaImage,
  FaPen,
  FaShieldAlt,
  FaTimes,
  FaTrashAlt,
  FaUserSlash,
} from "react-icons/fa";

import {
  getFullName,
  getMediaUrl,
  getRoleClass,
  getRoleLabel,
} from "../utils/leClubUtils";

const stopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onMouseDown={stopEvent}
      onClick={(event) => {
        stopEvent(event);
        onClick?.();
      }}
      className={`relative z-20 grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function UserSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="flex-1">
          <div className="h-5 w-40 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
          <div className="mt-3 h-4 w-52 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        </div>
      </div>
    </div>
  );
}

export default function UsersGrid({
  loading,
  users = [],
  deleteCandidate,
  onDetails,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}) {
  if (loading) {
    return (
      <section className="grid gap-4 xl:grid-cols-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <UserSkeleton key={index} />
        ))}
      </section>
    );
  }

  if (!users.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucun utilisateur trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou créez un nouvel utilisateur.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-4 xl:grid-cols-2">
      {users.map((user) => {
        const imageUrl = getMediaUrl(user.photoURL);
        const isDeleting =
          deleteCandidate?.type === "user" &&
          Number(deleteCandidate?.id) === Number(user.id);

        return (
          <article
            key={user.id}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            className="relative rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
          >
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-3xl bg-slate-100 dark:bg-slate-800">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={getFullName(user)}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-slate-400">
                    <FaImage className="text-2xl" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-xl font-bold text-slate-950 dark:text-white">
                    {getFullName(user)}
                  </h3>

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${getRoleClass(
                      user.role_id
                    )}`}
                  >
                    {getRoleLabel(user.role_id)}
                  </span>
                </div>

                <p className="mt-2 flex min-w-0 items-center gap-2 truncate text-sm text-slate-500 dark:text-slate-400">
                  <FaEnvelope className="shrink-0" />
                  <span className="truncate">{user.email || "Email non renseigné"}</span>
                </p>

                <p className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <FaShieldAlt />
                  Mot de passe non affiché
                </p>
              </div>
            </div>

            <div
              className="relative z-20 mt-5 flex flex-wrap justify-end gap-2"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              {isDeleting ? (
                <>
                  <button
                    type="button"
                    onMouseDown={stopEvent}
                    onClick={(event) => {
                      stopEvent(event);
                      onConfirmDelete(user.id);
                    }}
                    className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                  >
                    <FaCheck />
                    Confirmer
                  </button>

                  <button
                    type="button"
                    onMouseDown={stopEvent}
                    onClick={(event) => {
                      stopEvent(event);
                      onCancelDelete();
                    }}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <FaTimes />
                    Annuler
                  </button>
                </>
              ) : (
                <>
                  <IconButton
                    title="Voir le détail"
                    icon={FaEye}
                    tone="blue"
                    onClick={() => onDetails(user)}
                  />

                  <IconButton
                    title="Modifier"
                    icon={FaPen}
                    tone="orange"
                    onClick={() => onEdit(user)}
                  />

                  <IconButton
                    title="Supprimer"
                    icon={FaTrashAlt}
                    tone="rose"
                    onClick={() => onAskDelete(user.id)}
                  />
                </>
              )}
            </div>
          </article>
        );
      })}
    </section>
  );
}